import React, { useState, useEffect, useMemo } from 'react';
import { View, FlatList, StyleSheet, ActivityIndicator, Text, TouchableOpacity, Modal, TextInput, Platform } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, Club } from '../types';
import { adminService } from '../services/admin';
import { clubsService } from '../services/clubs';
import { useDialog } from '../contexts';
import { Button, ClubEditModal } from '../components';

type Props = NativeStackScreenProps<RootStackParamList, 'ManageClubs'>;

export const ManageClubsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [clubName, setClubName] = useState('');
  const [clubDescription, setClubDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClub, setEditingClub] = useState<Club | null>(null);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    const isAdmin = await adminService.isCurrentUserAdmin();
    if (!isAdmin) {
      await dialog.alert(
        t('common.errors.unauthorized'),
        t('screens.adminDashboard.notAuthorized'),
        'error'
      );
      navigation.goBack();
      return;
    }

    await loadData();
  };

  const loadData = async () => {
    try {
      const clubsData = await clubsService.getClubs();
      setClubs(clubsData);
    } catch (error: any) {
      await dialog.alert(
        t('common.errors.error'),
        error.message,
        'error'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  const handleCreateClub = async () => {
    if (!clubName.trim()) {
      await dialog.alert(
        t('common.errors.error'),
        t('screens.manageClubs.errorNameRequired'),
        'error'
      );
      return;
    }

    setCreateLoading(true);
    try {
      await clubsService.createClub({
        name: clubName.trim(),
        description: clubDescription.trim() || undefined,
      });

      await dialog.alert(
        t('common.success'),
        t('screens.manageClubs.successCreate'),
        'success'
      );

      setShowCreateModal(false);
      setClubName('');
      setClubDescription('');
      await loadData();
    } catch (error: any) {
      let errorMessage = error.message;
      if (errorMessage.includes('already exists')) {
        errorMessage = t('screens.manageClubs.errorDuplicateName');
      }
      await dialog.alert(
        t('common.errors.error'),
        errorMessage,
        'error'
      );
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditClub = (club: Club) => {
    setEditingClub(club);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updates: Partial<Club>) => {
    if (!editingClub) return;

    try {
      await clubsService.updateClub(editingClub.id, updates);
      await dialog.alert(
        t('common.success'),
        t('screens.manageClubs.successUpdate'),
        'success'
      );
      setShowEditModal(false);
      setEditingClub(null);
      await loadData();
    } catch (error: any) {
      let errorMessage = error.message;
      if (errorMessage.includes('already exists')) {
        errorMessage = t('screens.manageClubs.errorDuplicateName');
      }
      throw error; // Re-throw for ClubEditModal to handle
    }
  };

  const handleDeleteClub = async (club: Club) => {
    const confirmed = await dialog.confirm(
      t('screens.manageClubs.delete'),
      t('screens.manageClubs.confirmDelete'),
      { variant: 'danger' }
    );

    if (confirmed) {
      try {
        await clubsService.deleteClub(club.id);
        await dialog.alert(
          t('common.success'),
          t('screens.manageClubs.successDelete'),
          'success'
        );
        await loadData();
      } catch (error: any) {
        await dialog.alert(
          t('common.errors.error'),
          error.message,
          'error'
        );
      }
    }
  };

  // Filter clubs based on search query
  const filteredClubs = useMemo(() => {
    if (!searchQuery.trim()) {
      return clubs;
    }

    const query = searchQuery.toLowerCase();
    return clubs.filter(club => {
      const name = club.name.toLowerCase();
      const description = club.description?.toLowerCase() || '';

      return name.includes(query) || description.includes(query);
    });
  }, [clubs, searchQuery]);

  const renderClub = ({ item: club }: { item: Club }) => {
    return (
      <View style={styles.clubCard}>
        <View style={styles.clubInfo}>
          <Text style={styles.clubName}>{club.name}</Text>
          {club.description && (
            <Text style={styles.clubDescription}>{club.description}</Text>
          )}

          {/* Transaction Fee Info */}
          <View style={styles.feeInfoContainer}>
            <Text style={styles.feeLabel}>
              {t('screens.manageClubs.transactionFee')}
            </Text>
            <Text style={styles.feeValue}>€{club.transaction_fee_amount?.toFixed(2) || '2.00'}</Text>
            <Text style={styles.feeRange}>
              {t('screens.manageClubs.feeRange', {
                min: club.min_transaction_fee?.toFixed(2) || '1.00',
                max: club.max_transaction_fee?.toFixed(2) || '10.00',
              })}
            </Text>
          </View>
        </View>
        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.button, styles.editButton]}
            onPress={() => handleEditClub(club)}
          >
            <Text style={styles.buttonText}>
              {t('screens.manageClubs.edit')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.dangerButton]}
            onPress={() => handleDeleteClub(club)}
          >
            <Text style={styles.buttonText}>
              {t('screens.manageClubs.delete')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.header, Platform.OS === 'web' && styles.headerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <View style={styles.headerTitleRow}>
            <Text style={styles.headerTitle}>{t('screens.manageClubs.title')}</Text>
            <Button
              title={t('screens.manageClubs.createClub')}
              onPress={() => setShowCreateModal(true)}
              variant="primary"
            />
          </View>

          {/* Search Input */}
          <View style={styles.searchContainer}>
            <Ionicons name="search" size={20} color="#666" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder={t('screens.manageClubs.searchPlaceholder')}
              value={searchQuery}
              onChangeText={setSearchQuery}
              clearButtonMode="while-editing"
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearButton}>
                <Ionicons name="close-circle" size={20} color="#666" />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>

      <FlatList
        data={filteredClubs}
        renderItem={renderClub}
        keyExtractor={(item) => item.id}
        contentContainerStyle={[
          styles.list,
          Platform.OS === 'web' && styles.listWeb,
        ]}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        ListEmptyComponent={
          <Text style={styles.emptyText}>
            {searchQuery ? t('screens.manageClubs.noSearchResults') : t('screens.manageClubs.noClubs')}
          </Text>
        }
      />

      {/* Create Club Modal */}
      <Modal
        visible={showCreateModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowCreateModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('screens.manageClubs.createClub')}</Text>

            <Text style={styles.label}>{t('screens.manageClubs.clubName')}</Text>
            <TextInput
              style={styles.input}
              value={clubName}
              onChangeText={setClubName}
              placeholder={t('screens.manageClubs.clubName')}
              autoFocus
            />

            <Text style={styles.label}>{t('screens.manageClubs.clubDescription')}</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={clubDescription}
              onChangeText={setClubDescription}
              placeholder={t('screens.manageClubs.clubDescription')}
              multiline
              numberOfLines={4}
            />

            <View style={styles.modalActions}>
              <Button
                title={t('common.buttons.cancel')}
                onPress={() => {
                  setShowCreateModal(false);
                  setClubName('');
                  setClubDescription('');
                }}
                variant="secondary"
              />
              <Button
                title={t('common.buttons.create')}
                onPress={handleCreateClub}
                loading={createLoading}
                variant="primary"
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Club Modal */}
      {editingClub && (
        <ClubEditModal
          visible={showEditModal}
          club={editingClub}
          isAdmin={true} // ManageClubsScreen is admin-only
          onClose={() => {
            setShowEditModal(false);
            setEditingClub(null);
          }}
          onSave={handleSaveEdit}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerWeb: {
    alignItems: 'center',
  },
  headerContent: {
    width: '100%',
    maxWidth: 1200,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  backText: {
    fontSize: 16,
    color: '#007AFF',
    marginLeft: 4,
  },
  headerTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#333',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    outlineStyle: undefined,
  },
  clearButton: {
    padding: 4,
  },
  list: {
    padding: 16,
  },
  listWeb: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 1200,
  },
  clubCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  clubInfo: {
    marginBottom: 12,
  },
  clubName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  clubDescription: {
    fontSize: 14,
    color: '#666',
  },
  feeInfoContainer: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  feeLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
    marginBottom: 4,
  },
  feeValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
    marginBottom: 2,
  },
  feeRange: {
    fontSize: 11,
    color: '#999',
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  button: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  editButton: {
    backgroundColor: '#007AFF',
  },
  dangerButton: {
    backgroundColor: '#FF3B30',
  },
  buttonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyText: {
    textAlign: 'center',
    color: '#666',
    fontSize: 16,
    marginTop: 32,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 500,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 20,
    color: '#333',
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 16,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
});
